import React, { Suspense } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';

import AppConfig from '@/AppConfig';

import { Layout } from '@/components/Common/Layout';
import ErrorScreen from '@/components/Common/ErrorScreen/ErrorScreen';
import BaseElement from '@/pages/BaseElement';
import { Test } from '@/pages';
import AddFamilyMembers from '@/pages/add-family-member';
import ReviewDetails from '@/pages/review-details';
import RequestCreated from '@/pages/request-created';
import CallEndedFeedback from '@/pages/telehealth/feedback';
import RejoinCallPage from '@/pages/telehealth/call/rejoin';
import DisableTextSelectionWrapper from '@/components/Common/Wrapper/DisableTextSelectionWrapper';
import BaseTelehealth from '@/pages/telehealth/BaseTelehealth';
import BasePromo from '@/pages/promos/BasePromo';
import BaseCult from '@/pages/promos/cult-fit/BaseCult';

const Homepage = React.lazy(() => import('@/pages/homepage'));
const CreateClaim = React.lazy(() => import('@/pages/create'));
const ClaimsList = React.lazy(() => import('@/pages/list'));
const StatusPage = React.lazy(() => import('@/pages/status'));
const UploadPage = React.lazy(() => import('@/pages/upload/UploadPage'));
const StepDecider = React.lazy(() => import('@/pages/upload/StepDecider'));
const BaseUpload = React.lazy(() => import('@/pages/upload/BaseUpload'));
const NetworkHospitalsV2 = React.lazy(
  () => import('@/pages/network-hospitals/v2/index.tsx'),
);
const TelehealthHome = React.lazy(() => import('@/pages/telehealth'));

const TelehealthCallUI = React.lazy(
  () => import('@/pages/telehealth/call/index'),
);
const CultLandingPage = React.lazy(
  () => import('@/pages/promos/cult-fit/LandingPage'),
);
const CultCheckoutPage = React.lazy(
  () => import('@/pages/promos/cult-fit/CheckoutPage'),
);
const CultCheckoutStatusPage = React.lazy(
  () => import('@/pages/promos/cult-fit/CheckoutStatusPage'),
);

const suspenseFallback = (
  <Layout.Main
    headerProps={{
      hideBack: true,
    }}
    loading
  />
);

const router = createBrowserRouter([
  {
    path: AppConfig.BASE_PATH,
    element: (
      <Suspense fallback={suspenseFallback}>
        <BaseElement />
      </Suspense>
    ),
    errorElement: (
      <ErrorScreen layoutProps={{ headerProps: { hidePageHeader: true } }} />
    ),
    children: [
      {
        path: '',
        async lazy() {
          return {
            element: <Homepage />,
          };
        },
      },
      {
        path: 'claims',
        children: [
          {
            path: 'create',
            element: <CreateClaim />,
          },
          {
            path: 'all',
            element: <ClaimsList />,
          },
          {
            path: ':claimId',
            children: [
              {
                path: '',
                element: <StatusPage />,
              },
              {
                path: 'upload',
                element: <BaseUpload />,
                children: [
                  {
                    path: '',
                    element: <StepDecider />,
                  },
                  {
                    path: ':step',
                    element: <UploadPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'network-hospitals',
        element: <NetworkHospitalsV2 />,
      },
      {
        path: 'family-addition',
        element: (
          <DisableTextSelectionWrapper>
            <Outlet />
          </DisableTextSelectionWrapper>
        ),
        children: [
          {
            path: 'add-family-member',
            element: <AddFamilyMembers />,
          },
          {
            path: 'review-details',
            element: <ReviewDetails />,
          },
          {
            path: 'request-created',
            element: <RequestCreated />,
          },
        ],
      },
      {
        path: 'retail',
        element: <BasePromo />,
        children: [
          {
            path: 'cult-fit',
            element: <BaseCult />,
            children: [
              {
                path: '',
                element: <CultLandingPage />,
              },
              {
                path: 'checkout',
                element: <CultCheckoutPage />,
              },
              {
                path: 'status',
                element: <CultCheckoutStatusPage />,
              },
            ],
          },
        ],
      },
      {
        path: 'test',
        element: <Test />,
      },
      {
        path: '/telehealth',
        element: <BaseTelehealth />,
        errorElement: (
          <ErrorScreen
            layoutProps={{ headerProps: { hidePageHeader: true } }}
          />
        ),
        children: [
          { path: '', element: <TelehealthHome /> },
          { path: 'call', element: <TelehealthCallUI /> },
          { path: 'call/rejoin', element: <RejoinCallPage /> },
          { path: 'feedback', element: <CallEndedFeedback /> },
        ],
      },
    ],
  },
]);

export { router };
